// Auto-position a freshly-favorited take on the Master timeline.
// Ported from legacy `autoPositionFavOnTimeline` (index.html line 7600).
//
// Strategy:
//  - If the block is NOT user-pinned (`pinned !== true`), place it at the
//    auto-anchor: `max(offsets[i]+dur[i]) for i<blockIdx with fav`,
//    snapped up to the 0.1-second raster via `Math.ceil(x*10)/10`.
//    (Vanilla gated on `offset > 0`, but a stale offset survives fav
//    removal — using the explicit `pinned` flag means a re-added fav
//    after Reset Positions or a fav-toggle goes through auto-position.)
//  - Cascade forward: any later fav'd block whose current offset starts
//    before the newly-placed block's end is pushed just past it. This runs
//    even when the block was pinned, because a longer new take may shove
//    neighbours further right.
//
// Metadata-race guard: the caller must ensure durations are populated before
// calling this helper. Legacy handled this by deferring through a
// `loadedmetadata` listener — TTSView mirrors that pattern. If a duration is
// still 0/NaN here we treat it as 0 (best effort) rather than NaN-contaminate
// the offsets; the caller will re-run us once metadata lands.

import type { Block } from '@shared/types';
import { chipTrackVoiceId } from './timeline';

function snapUp(x: number): number {
  return Math.ceil(x * 10) / 10;
}

function durationOf(b: Block | undefined): number {
  if (!b) return 0;
  const t = b.takes[b.favIdx];
  if (!t) return 0;
  const d = t.duration;
  return Number.isFinite(d) && d > 0 ? d : 0;
}

/**
 * Place `blockIdx` at its auto-anchor + cascade later blocks. Returns a new
 * blocks array; only offsets change (pinned flag is preserved).
 *
 * Multi-Voice (#173): anchor + cascade only look at blocks on the SAME voice
 * track as the target. Cross-voice chips are free to overlap (dialog effect),
 * so they don't push the target around and the target doesn't push them.
 *
 * `assetVoiceIds` is required so chipTrackVoiceId can fall back when block /
 * take voiceIds are missing. Pre-multi-voice callers can pass `[]` — with an
 * empty list every block resolves to the same fallback voice ('') and the
 * function behaves exactly like before this change.
 *
 * @param blockIdx       Block that just had its favorite set.
 * @param blocks         Current blocks snapshot.
 * @param assetVoiceIds  Asset's allowed voice IDs (for track-grouping fallback).
 * @returns              Updated blocks array (same length, new offset values).
 */
export function autoPositionFavOnTimeline(
  blockIdx: number,
  blocks: Block[],
  assetVoiceIds: string[] = [],
): Block[] {
  const target = blocks[blockIdx];
  if (!target) return blocks;
  if (target.favIdx < 0) return blocks;

  // Nur Blöcke, deren Offset sich wirklich ändert, werden neu angelegt —
  // alle anderen behalten ihre Referenz (Audit M7: der Voll-Klon ließ
  // `indexOf(block)` nach jedem Stern-Klick ins Leere laufen).
  const next: Block[] = blocks.slice();
  const setOffset = (i: number, offset: number) => {
    if (next[i].offset === offset) return;
    next[i] = { ...next[i], offset };
  };

  const targetVoice = chipTrackVoiceId(next[blockIdx], assetVoiceIds);

  // Auto-anchor unless the block was explicitly pinned by a user drag.
  // Stale offsets that survive a fav-removal no longer prevent the
  // re-add from being auto-placed. Multi-Voice: only same-track chips
  // anchor this one — cross-voice chips can be at any offset.
  const isPinned = next[blockIdx].pinned === true;
  if (!isPinned) {
    let anchor = 0;
    for (let i = 0; i < blockIdx; i++) {
      const b = next[i];
      if (chipTrackVoiceId(b, assetVoiceIds) !== targetVoice) continue;
      const d = durationOf(b);
      if (d <= 0) continue;
      const end = (b.offset || 0) + d;
      if (end > anchor) anchor = end;
    }
    setOffset(blockIdx, snapUp(anchor));
  }

  // Cascade: push later fav'd blocks on the same voice-track past our end if
  // they'd overlap. Cross-voice chips remain untouched.
  const selfDur = durationOf(next[blockIdx]);
  let cursor = (next[blockIdx].offset || 0) + selfDur;
  for (let i = blockIdx + 1; i < next.length; i++) {
    const b = next[i];
    if (chipTrackVoiceId(b, assetVoiceIds) !== targetVoice) continue;
    const d = durationOf(b);
    if (d <= 0) continue;
    if ((b.offset || 0) < cursor) {
      setOffset(i, snapUp(cursor));
    }
    cursor = (next[i].offset || 0) + d;
  }

  return next;
}
